/* ==========================================================================
   IT Factory Australia Custom Cart
   File: assets/itf-cart.js
   Scope: Custom cart page only
   ========================================================================== */

(function () {
  'use strict';

  const SELECTORS = {
    cart: '[data-itf-cart-section]',
    quantityInput: '[data-itf-cart-quantity]',
    decrease: '[data-itf-cart-decrease]',
    increase: '[data-itf-cart-increase]',
    remove: '[data-itf-cart-remove]',
    add: '[data-itf-cart-add]',
    note: '[data-itf-cart-note]',
    status: '[data-itf-cart-status]',
    message: '[data-itf-cart-message]',
    lineItem: '[data-itf-cart-line]',
    headerCart: '#cart-icon-bubble',
    headerCartCount: '.itf-header__cart-count',
    drawerRoot: 'cart-drawer',
    drawerContent: '#CartDrawer',
    drawerOverlay: '#CartDrawer-Overlay'
  };

  const CLASSES = {
    updating: 'is-updating',
    cartUpdating: 'is-cart-updating',
    messageVisible: 'is-visible',
    messageError: 'is-error',
    messageSuccess: 'is-success'
  };

  function debounce(fn, wait) {
    let timeout;

    return function debounced(...args) {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function getRoutes() {
    return window.routes || {};
  }

  function getCartError() {
    return window.cartStrings?.error || 'Something went wrong while updating your cart.';
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function getCartJSON() {
    return fetch(`${getRoutes().cart_url}.js`, {
      headers: {
        Accept: 'application/json'
      }
    }).then((response) => {
      if (!response.ok) {
        throw new Error(getCartError());
      }

      return response.json();
    });
  }

  function fetchJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    }).then((response) => {
      return response.json().then((json) => {
        if (!response.ok || json.status) {
          const message = json.description || json.message || getCartError();
          throw new Error(message);
        }

        return json;
      });
    });
  }

  function refreshHeaderCartCount(cartState) {
    const cartLink = document.querySelector(SELECTORS.headerCart);

    if (!cartLink || !cartState) return;

    const count = Number(cartState.item_count || 0);
    const cartLabel = cartLink.querySelector('.itf-header__cart-label, .itf-header__cart-label--visible');
    const labelText = cartLabel ? cartLabel.textContent.trim() : 'Cart';

    cartLink.setAttribute(
      'aria-label',
      `${labelText}${count > 0 ? `, ${count} item${count === 1 ? '' : 's'}` : ''}`
    );

    let countBubble = cartLink.querySelector(SELECTORS.headerCartCount);
    let hiddenCount = cartLink.querySelector('[data-itf-header-cart-count-text]');

    if (count > 0) {
      if (!countBubble) {
        countBubble = document.createElement('span');
        countBubble.className = 'itf-header__cart-count';
        countBubble.setAttribute('aria-hidden', 'true');

        if (cartLabel) {
          cartLabel.insertAdjacentElement('afterend', countBubble);
        } else {
          cartLink.appendChild(countBubble);
        }
      }

      countBubble.textContent = count < 100 ? String(count) : '99+';

      if (!hiddenCount) {
        hiddenCount = document.createElement('span');
        hiddenCount.className = 'itf-header__visually-hidden';
        hiddenCount.setAttribute('data-itf-header-cart-count-text', '');
        cartLink.appendChild(hiddenCount);
      }

      hiddenCount.textContent = `${count} item${count === 1 ? '' : 's'} in cart`;
    } else {
      if (countBubble) countBubble.remove();
      if (hiddenCount) hiddenCount.remove();
    }
  }

  function refreshCartDrawer(cartState) {
    if (!cartState || !cartState.sections || !cartState.sections['cart-drawer']) return;

    const drawerRoot = document.querySelector(SELECTORS.drawerRoot);
    const currentDrawer = document.querySelector(SELECTORS.drawerContent);

    if (!drawerRoot || !currentDrawer) return;

    const drawerDocument = parseHTML(cartState.sections['cart-drawer']);
    const newDrawer = drawerDocument.querySelector(SELECTORS.drawerContent);

    if (!newDrawer) return;

    currentDrawer.innerHTML = newDrawer.innerHTML;
    drawerRoot.classList.toggle('is-empty', Number(cartState.item_count || 0) === 0);

    const overlay = drawerRoot.querySelector(SELECTORS.drawerOverlay);

    if (overlay && typeof drawerRoot.close === 'function') {
      overlay.addEventListener('click', drawerRoot.close.bind(drawerRoot));
    }
  }

  function publishCartUpdate(cartState) {
    if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'itf-cart',
        cartData: cartState
      });
    }

    document.dispatchEvent(
      new CustomEvent('itf:cart:updated', {
        bubbles: true,
        detail: {
          cart: cartState
        }
      })
    );
  }

  class ITFCart extends HTMLElement {
    constructor() {
      super();

      this.sectionId = this.dataset.sectionId;
      this.pendingLine = null;
      this.noteHandler = debounce(this.updateNote.bind(this), 700);
    }

    connectedCallback() {
      this.bindEvents();
    }

    bindEvents() {
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));

      const note = this.querySelector(SELECTORS.note);

      if (note) {
        note.addEventListener('input', this.noteHandler);
      }
    }

    getSectionsToRender() {
      const sections = [];

      if (this.sectionId) {
        sections.push(this.sectionId);
      }

      if (document.querySelector(SELECTORS.drawerRoot)) {
        sections.push('cart-drawer');
      }

      return sections;
    }

    onClick(event) {
      const decrease = event.target.closest(SELECTORS.decrease);
      const increase = event.target.closest(SELECTORS.increase);
      const remove = event.target.closest(SELECTORS.remove);
      const add = event.target.closest(SELECTORS.add);

      if (decrease) {
        event.preventDefault();
        this.stepQuantity(decrease, -1);
      }

      if (increase) {
        event.preventDefault();
        this.stepQuantity(increase, 1);
      }

      if (remove) {
        event.preventDefault();

        const line = Number(remove.dataset.line);

        if (line > 0) {
          this.changeLine(line, 0);
        }
      }

      if (add) {
        event.preventDefault();
        this.addItem(add);
      }
    }

    onChange(event) {
      const input = event.target.closest(SELECTORS.quantityInput);

      if (!input) return;

      const line = Number(input.dataset.line);
      const quantity = this.normaliseQuantity(input);

      if (line > 0) {
        this.changeLine(line, quantity);
      }
    }

    stepQuantity(button, direction) {
      const line = Number(button.dataset.line);
      const input = this.querySelector(`${SELECTORS.quantityInput}[data-line="${line}"]`);

      if (!input) return;

      const current = Number(input.value || 0);
      const step = Number(input.step || input.dataset.step || 1);
      const next = current + step * direction;

      input.value = this.normaliseQuantity(input, next);

      if (line > 0) {
        this.changeLine(line, Number(input.value));
      }
    }

    normaliseQuantity(input, forcedValue) {
      const rawValue = typeof forcedValue === 'number' ? forcedValue : Number(input.value || 0);
      const min = Number(input.min || input.dataset.min || 0);
      const max = input.max || input.dataset.max;
      const step = Number(input.step || input.dataset.step || 1);

      let value = Number.isNaN(rawValue) ? min : rawValue;

      value = Math.max(0, value);

      if (max !== undefined && max !== null && max !== '') {
        value = Math.min(value, Number(max));
      }

      if (step > 1 && value > 0) {
        value = Math.round(value / step) * step;
      }

      return value;
    }

    setCartLoading(isLoading) {
      this.classList.toggle(CLASSES.cartUpdating, isLoading);
      this.setAttribute('aria-busy', isLoading ? 'true' : 'false');

      const actionElements = this.querySelectorAll(
        `${SELECTORS.decrease}, ${SELECTORS.increase}, ${SELECTORS.remove}, ${SELECTORS.add}, ${SELECTORS.quantityInput}`
      );

      actionElements.forEach((element) => {
        if (isLoading) {
          element.setAttribute('data-itf-was-disabled', element.disabled ? 'true' : 'false');
          element.disabled = true;
        } else if (element.getAttribute('data-itf-was-disabled') !== 'true') {
          element.disabled = false;
          element.removeAttribute('data-itf-was-disabled');
        }
      });
    }

    showMessage(message, type = 'success') {
      const messageBox = this.querySelector(SELECTORS.message);

      if (!messageBox) return;

      messageBox.textContent = message;
      messageBox.hidden = false;
      messageBox.classList.add(CLASSES.messageVisible);
      messageBox.classList.toggle(CLASSES.messageError, type === 'error');
      messageBox.classList.toggle(CLASSES.messageSuccess, type !== 'error');

      window.clearTimeout(this.messageTimeout);

      this.messageTimeout = window.setTimeout(() => {
        messageBox.classList.remove(CLASSES.messageVisible, CLASSES.messageError, CLASSES.messageSuccess);
        messageBox.hidden = true;
        messageBox.textContent = '';
      }, 3500);
    }

    changeLine(line, quantity) {
      const routes = getRoutes();

      if (!routes.cart_change_url) {
        this.showGlobalError(getCartError());
        return;
      }

      const lineItem = this.querySelector(`${SELECTORS.lineItem}[data-itf-cart-line="${line}"]`);

      this.pendingLine = line;
      this.setCartLoading(true);
      this.setLineLoading(lineItem, true);
      this.clearLineError(line);

      fetchJSON(routes.cart_change_url, {
        line,
        quantity,
        sections: this.getSectionsToRender(),
        sections_url: window.location.pathname
      })
        .then((cartState) => {
          this.announce(quantity === 0 ? 'Item removed from cart.' : 'Cart updated.');
          this.showMessage(quantity === 0 ? 'Item removed from cart.' : 'Cart updated.');
          this.renderUpdatedCart(cartState);
          refreshHeaderCartCount(cartState);
          refreshCartDrawer(cartState);
          publishCartUpdate(cartState);
        })
        .catch((error) => {
          this.setCartLoading(false);
          this.setLineLoading(lineItem, false);
          this.showLineError(line, error.message || getCartError());
          this.showMessage(error.message || getCartError(), 'error');
        });
    }

    addItem(button) {
      const routes = getRoutes();
      const variantId = Number(button.dataset.variantId);
      const quantity = Number(button.dataset.quantity || 1);

      if (!routes.cart_add_url || !variantId) {
        this.showGlobalError(getCartError());
        return;
      }

      let properties = {};

      if (button.dataset.properties) {
        try {
          properties = JSON.parse(button.dataset.properties);
        } catch (error) {
          properties = {};
        }
      }

      this.setCartLoading(true);
      button.disabled = true;
      button.classList.add(CLASSES.updating);

      fetchJSON(routes.cart_add_url, {
        items: [
          {
            id: variantId,
            quantity,
            properties
          }
        ],
        sections: this.getSectionsToRender(),
        sections_url: window.location.pathname
      })
        .then((sectionState) => {
          this.announce('Item added to cart.');
          this.showMessage('Item added to cart.');
          this.renderUpdatedCart(sectionState);
          refreshCartDrawer(sectionState);

          return getCartJSON();
        })
        .then((cartState) => {
          refreshHeaderCartCount(cartState);
          publishCartUpdate(cartState);
        })
        .catch((error) => {
          this.setCartLoading(false);
          button.disabled = false;
          button.classList.remove(CLASSES.updating);
          this.showGlobalError(error.message || getCartError());
          this.showMessage(error.message || getCartError(), 'error');
        });
    }

    updateNote(event) {
      const routes = getRoutes();
      const note = event.target.value;

      if (!routes.cart_update_url) return;

      fetchJSON(routes.cart_update_url, {
        note
      })
        .then(() => {
          this.announce('Order note updated.');
          this.showMessage('Order note updated.');
        })
        .catch(() => {
          this.announce(getCartError());
          this.showMessage(getCartError(), 'error');
        });
    }

    renderUpdatedCart(cartState) {
      if (!cartState || !cartState.sections || !this.sectionId) return;

      const sectionHTML = cartState.sections[this.sectionId];

      if (!sectionHTML) {
        window.location.reload();
        return;
      }

      const sectionDocument = parseHTML(sectionHTML);
      const newCart = sectionDocument.querySelector(SELECTORS.cart);

      if (!newCart) {
        window.location.reload();
        return;
      }

      this.outerHTML = newCart.outerHTML;
    }

    setLineLoading(lineItem, isLoading) {
      if (!lineItem) return;

      lineItem.classList.toggle(CLASSES.updating, isLoading);
      lineItem.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    clearLineError(line) {
      const error = this.querySelector(`[data-itf-cart-line-error="${line}"]`);

      if (error) {
        error.textContent = '';
      }
    }

    showLineError(line, message) {
      const error = this.querySelector(`[data-itf-cart-line-error="${line}"]`);

      if (error) {
        error.textContent = message;
      }

      this.announce(message);
      this.showMessage(message, 'error');
    }

    showGlobalError(message) {
      this.announce(message);
      this.showMessage(message, 'error');
    }

    announce(message) {
      const status = this.querySelector(SELECTORS.status);

      if (!status) return;

      status.textContent = '';

      window.setTimeout(() => {
        status.textContent = message;
      }, 20);
    }
  }

  if (!customElements.get('itf-cart')) {
    customElements.define('itf-cart', ITFCart);
  }
})();